import { useContext, useEffect, useRef, useState } from 'react';
import { useBlocker, useLocation, useParams } from 'react-router-dom';
import { useForm, useWatch } from 'react-hook-form';
import axios, { AxiosError } from 'axios';
import Cookie from 'js-cookie';
import { yupResolver } from '@hookform/resolvers/yup';
import {
  IconLoader2,
  IconSquareRoundedNumber1Filled,
  IconSquareRoundedNumber2Filled,
  IconSquareRoundedNumber3Filled,
  IconDeviceFloppy,
  IconCircleCheck,
  IconSquareRoundedNumber4Filled,
  IconSquareRoundedNumber5Filled,
  IconAlertCircle,
} from '@tabler/icons-react';
import useTheme from 'chat-ui/src/hooks/useTheme';
import useChatStore from 'chat-ui/src/store';
import { toast } from 'react-toastify';
import useChatSettingsStore from 'chat-ui/src/ChatSettingsStore';
import { getErrorMessage } from '@common/utils';
import { EProjectDesignSection, EProjectStatus, EStatusCode } from '@/enums';
import { MessageCircle } from 'lucide-react';
import DashboardTestChat from '@/components/chat/DashboardTestChat';
import useMediaQuery from '@/hooks/useMediaQuery';
import { Sheet, SheetContent, SheetTitle } from '@/components/ui/sheet';
import Context from '@/components/projects/single-project/research-design/Context';
import Instructions from '@/components/projects/single-project/research-design/Instructions';
import { DiscussionGuide } from '@/components/projects/single-project/research-design/DiscussionGuide';
import { Skeleton } from '@/components/ui/skeleton';
import { LangContext } from '@/context/LangProvider';
import useErrorStore from '@/store/ErrorStore';
import useQuestionsStore from '@/store/ScriptQuestionsStore';
import TestChatSkeletonLoading from './DashboardTestChatSkeletonLoading';
import { useMutateProjectUpdate } from '@/reactQuery/post';
import { Button } from '@/components/ui/button';
import useWorkspaceRoles from '@/hooks/useWorkspaceRoles';
import useCreateProjectStore from '@/store/CreateProjectStore';
import UnsavedChangesModal from '@/components/Modals/UnsavedChangesModal';
import { useFormButtonText } from '@/hooks/useFormButtonText';
import ScreeningQuestions from '@/components/projects/single-project/research-design/ScreeningQuestions';
import useScreeningQuestionsStore from '@/store/ScreeningQuestionsStore';
import useProjectRequiredFieldsStore from '@/store/ProjectRequiredFieldsStore';
import {
  ProjectCreationValues,
  QuestionsFilesData,
  ScreeningQuestion,
  InterviewQuestions,
} from '@/types';
import { useGetSingleProjectData } from '@/reactQuery/get';
import ProjectShareMaxRespondets from '@/components/projects/single-project/share/ProjectShareMaxRespondents';
import { artificialDelay } from '@/utils/artificialDelay';
import ToastMessage from '@/components/toast-message/ToastMessage';
import { hasEmptyQuestions, isInterviewQuestionsEmpty } from '@/utils/questionGroupHelpers';
import { createProjectValidationSchema } from '@/schemas';

function ProjectCreate() {
  const { lang } = useContext(LangContext);
  const { currentTheme } = useTheme();
  const { pathname } = useLocation();
  // Below lg the test chat sits behind a trigger button and opens in a
  // right-side Sheet slide-over so the design form gets the full viewport
  // width. Crossing the lg breakpoint mid-test will unmount the sheet
  // contents — documented as an accepted tradeoff.
  const isLg = useMediaQuery('(min-width: 1024px)');
  const [testChatSheetOpen, setTestChatSheetOpen] = useState(false);
  const {
    project_id: projectId,
    organization_id: organizationId,
    workspace_id: workspaceId,
  } = useParams();
  const { isUserViewer } = useWorkspaceRoles();
  const { questionsLength } = useQuestionsStore();
  const { screeningQuestionsLength, screeningAnswersLength } = useScreeningQuestionsStore();
  const { setShowError } = useErrorStore();
  const {
    isFormChanged,
    setIsFormChanged,
    isMediaReadyForUpload,
    setIsMediaReadyForUpload,
    uploadingQuestionId,
    setUploadingQuestionId,
    reorderChanged,
    setReorderChanged,
    interviewQuestionsStructureChanged,
    setInterviewQuestionsStructureChanged,
    isProjectCreateButtonDisabled,
  } = useCreateProjectStore();
  const [activeSection, setActiveSection] = useState<EProjectDesignSection | null>(
    EProjectDesignSection.INSTRUCTIONS,
  );
  const discussionGuideSectionRef = useRef<HTMLDivElement>(null);
  const screeningSectionRef = useRef<HTMLDivElement>(null);
  const setInitialMessages = useChatStore((state) => state.setInitialMessages);
  const setCurrentConversationId = useChatStore((state) => state.setCurrentConversationId);
  const [fileData, setFileData] = useState<{ [key: string]: QuestionsFilesData }>({});
  const { setChatSettingsData } = useChatSettingsStore();
  const {
    data: singleProjectData,
    error: projectDataError,
    refetch,
    isLoading: isProjectDataLoading,
  } = useGetSingleProjectData(organizationId!, workspaceId!, projectId!);

  const minNumber = singleProjectData?.respondents_max;

  const blocker = useBlocker(
    ({ currentLocation, nextLocation }) =>
      isFormChanged && currentLocation.pathname !== nextLocation.pathname,
  );

  const handleToggleSection = (section: EProjectDesignSection) => {
    setActiveSection((prev) => (prev === section ? null : section));
  };

  const {
    register,
    control,
    setValue,
    watch,
    reset,
    getValues,
    formState: { errors, dirtyFields },
    clearErrors,
    trigger,
  } = useForm({
    defaultValues: {
      type_of_interview: singleProjectData?.type_of_interview || [],
      interviewer_name: singleProjectData?.interviewer_name || '',
      company_name: singleProjectData?.company_name || '',
      interview_goal: singleProjectData?.interview_goal || '',
      interview_audience: singleProjectData?.interview_audience || '',
      tone_of_voice: singleProjectData?.tone_of_voice || '',
      language: singleProjectData?.language || '',
      allow_language_selection: singleProjectData?.allow_language_selection || null,
      privacy_policy: singleProjectData?.privacy_policy || '',
      analysis_language:
        singleProjectData?.analysis_language !== ''
          ? singleProjectData?.analysis_language
          : 'English',
      theme_id: singleProjectData?.theme_id || '',
      snippets: singleProjectData?.snippets || [],
      opening_message: singleProjectData?.opening_message || '',
      respondents_max: singleProjectData?.respondents_max || null,
      interview_questions: singleProjectData?.interview_questions || undefined,
      screening_questions:
        singleProjectData?.screening_questions?.map((question: ScreeningQuestion) => ({
          ...question,
        })) || [],
    },

    resolver: yupResolver(
      createProjectValidationSchema(
        minNumber || 0,
        singleProjectData?.status === EProjectStatus.PUBLISHED ||
          singleProjectData?.status === EProjectStatus.COMPLETED,
      ),
    ),
    mode: 'onSubmit',
  });

  const { mutateAsync, status } = useMutateProjectUpdate(organizationId, workspaceId, projectId);
  const { getButtonText } = useFormButtonText();
  const isProjectPublished = singleProjectData?.status === EProjectStatus.PUBLISHED;
  const isProjectCompleted = singleProjectData?.status === EProjectStatus.COMPLETED;
  const currentQuestions = useWatch({ name: 'interview_questions', control });
  const isInstructionsOpen = activeSection === EProjectDesignSection.INSTRUCTIONS;
  const isContextOpen = activeSection === EProjectDesignSection.CONTEXT;
  const isDiscussionGuideOpen = activeSection === EProjectDesignSection.DISCUSSION_GUIDE;
  const isMaxRespondentsOpen = activeSection === EProjectDesignSection.MAX_RESPONDENTS;
  const isScreeningOpen = activeSection === EProjectDesignSection.SCREENING;

  const { requiredFields, setRequiredFields } = useProjectRequiredFieldsStore();

  // Watch all form values to detect changes in required fields
  const allFormValues = watch();

  const watchedValuesArray = requiredFields.map((field) => {
    const value = allFormValues[field.key as keyof typeof allFormValues];
    if (field.key === 'interview_questions') {
      // Serialize the entire interview_questions structure to detect any changes
      // This ensures changes in nested groups/questions trigger the validation
      return JSON.stringify(value || '');
    }
    return value || '';
  });
  const watchedJoinValues = watchedValuesArray.join(',');

  const getOnlyEditedData = (data: ProjectCreationValues) => {
    const editedData = Object.keys(dirtyFields).reduce(
      (acc, key) => {
        acc[key] = data[key as keyof ProjectCreationValues];
        return acc;
      },
      {} as Record<string, unknown>,
    );

    const shouldIncludeInterviewQuestions =
      data.interview_questions &&
      !('interview_questions' in editedData) &&
      (reorderChanged ||
        interviewQuestionsStructureChanged ||
        !singleProjectData?.interview_questions); // new project

    if (shouldIncludeInterviewQuestions) {
      editedData.interview_questions = data.interview_questions;
    }

    return editedData;
  };

  const onSubmit = async (data: ProjectCreationValues, skipValidation: boolean = false) => {
    if (isUserViewer) return;

    // When skipValidation is true, we're doing a direct update (like deletion) that bypasses validation
    // Use skipValidation flag directly instead of trying to infer intent from payload structure
    const payload = skipValidation
      ? {
          ...(data.interview_questions ? { interview_questions: data.interview_questions } : {}),
          ...(data.screening_questions ? { screening_questions: data.screening_questions } : {}),
        }
      : getOnlyEditedData(data);

    // Check if payload is empty
    if (Object.keys(payload).length === 0) {
      return;
    }

    reset({}, { keepValues: true });

    try {
      await artificialDelay();

      await mutateAsync({
        _id: projectId,
        ...payload,
      });

      if (isMediaReadyForUpload) {
        const mediaUploadPromises = Object.keys(fileData).map(async (questionId) => {
          const questionMedia = fileData[questionId].file;
          if (questionMedia) {
            setUploadingQuestionId(questionId);
            try {
              await axios.post(
                `${
                  import.meta.env.VITE_API_BASE_URL
                }/organizations/${organizationId}/workspaces/${workspaceId}/projects/${projectId}/media/${questionId}`,
                { media: questionMedia },
                {
                  headers: {
                    'Content-Type': 'multipart/form-data',
                    Authorization: `Bearer ${Cookie.get('token')}`,
                  },
                },
              );
            } catch {
              toast.error(
                <ToastMessage
                  description="unsupportedMediaTypeDescription"
                  title="unsupportedMediaType"
                />,
                { position: 'top-center' },
              );
            } finally {
              setUploadingQuestionId(null);
            }
          }
        });

        await Promise.all(mediaUploadPromises);
      }

      if ('type_of_interview' in data) {
        setChatSettingsData(data?.type_of_interview as string[]);
      }

      setReorderChanged(false);
      setInterviewQuestionsStructureChanged(false);
      setIsFormChanged(false);
      setIsMediaReadyForUpload(false);

      await refetch();
    } catch (error) {
      const axiosError = error as AxiosError;
      const message = getErrorMessage(axiosError);
      toast.error(message);
      throw axiosError;
    }
  };

  useEffect(() => {
    if (singleProjectData) {
      const fields = [
        'type_of_interview',
        'interviewer_name',
        'company_name',
        'interview_goal',
        'interview_audience',
        'tone_of_voice',
        'privacy_policy',
        'language',
        'allow_language_selection',
        'analysis_language',
        'theme_id',
        'opening_message',
        'interview_questions',
        'screening_questions',
        'respondents_max',
      ];

      reset(
        fields.reduce<Record<string, unknown>>((acc, key) => {
          const projectKey = key as keyof typeof singleProjectData;
          acc[key] =
            key === 'respondents_max' && singleProjectData[projectKey] === 0
              ? null
              : singleProjectData[projectKey];
          return acc;
        }, {}),
      );
      if (singleProjectData.snippets?.length) {
        setValue('snippets', singleProjectData.snippets);
      } else {
        setValue('snippets', ['']);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setValue, singleProjectData]);

  useEffect(() => {
    if (
      (projectDataError as AxiosError)?.status === EStatusCode.BAD_REQUEST ||
      (projectDataError as AxiosError)?.status === EStatusCode.PROJECT_NOT_FOUND
    ) {
      setShowError(true);
    }
  }, [projectDataError, setShowError]);

  useEffect(() => {
    const handlePageLeave = () => {
      setInitialMessages([]);
      setCurrentConversationId('');
    };

    return () => {
      handlePageLeave();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname]);

  useEffect(() => {
    if (errors?.privacy_policy?.message) {
      if (!isInstructionsOpen) {
        handleToggleSection(EProjectDesignSection.INSTRUCTIONS);

        const scrollAfterToggle = () => {
          const scrollContainer = document.querySelector('#scrollContainer');
          if (scrollContainer) {
            scrollContainer.scrollTop = scrollContainer.scrollHeight;
          }
        };

        window.requestAnimationFrame(() => {
          setTimeout(scrollAfterToggle, 400);
        });
        toast.error('Privacy policy must be a valid link.', { position: 'top-center' });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [errors, isInstructionsOpen]);

  useEffect(() => {
    const checkFieldEmptyStatus = () => {
      const updatedFields = requiredFields.map((field) => {
        const fieldValue = allFormValues[field.key as keyof typeof allFormValues];

        let isEmpty: boolean;
        if (field.key === 'allow_language_selection') {
          isEmpty = fieldValue === null;
        } else if (field.key === 'interview_questions') {
          const questionsStructure = fieldValue as InterviewQuestions | undefined;
          isEmpty = isInterviewQuestionsEmpty(questionsStructure);
        } else {
          isEmpty = !fieldValue;
        }

        return { ...field, isEmpty };
      });
      setRequiredFields(updatedFields);
    };

    const debounceTimer = setTimeout(checkFieldEmptyStatus, 300);
    return () => clearTimeout(debounceTimer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [watchedJoinValues]);

  if (blocker.state === 'blocked') return <UnsavedChangesModal blocker={blocker} />;

  // Check if any question in the group structure has empty text
  const hasEmptyQuestion = currentQuestions ? hasEmptyQuestions(currentQuestions) : false;
  const hasInterviewQuestionsError = 'interview_questions' in errors;
  const showQuestionsPanelWarning =
    (hasEmptyQuestion || hasInterviewQuestionsError) && !isDiscussionGuideOpen;

  // Check for screening questions errors
  const hasScreeningQuestionsError = 'screening_questions' in errors;
  const showScreeningPanelWarning = hasScreeningQuestionsError && !isScreeningOpen;

  // Calculate submit button disabled state
  const isPending = status === 'pending';
  const isIdleWithoutChanges =
    status === 'idle' && !isFormChanged && !reorderChanged && !interviewQuestionsStructureChanged;
  const isSuccessWithoutChanges =
    status === 'success' &&
    !isFormChanged &&
    !reorderChanged &&
    !interviewQuestionsStructureChanged;
  const hasRespondentsMaxError = Object.keys(errors).includes('respondents_max');

  const isSubmitDisabled =
    isPending ||
    isProjectCreateButtonDisabled ||
    !!uploadingQuestionId ||
    isUserViewer ||
    hasRespondentsMaxError ||
    isIdleWithoutChanges ||
    isSuccessWithoutChanges;

  const onError = (formErrors: typeof errors) => {
    // Handle screening questions errors
    if (formErrors.screening_questions) {
      toast.error(lang.get('msg.fixValidationErrors'));

      // Automatically open the Screening section
      if (!isScreeningOpen) {
        handleToggleSection(EProjectDesignSection.SCREENING);
      }

      setTimeout(() => {
        if (screeningSectionRef.current) {
          screeningSectionRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 300);
      return;
    }

    // Handle interview questions errors
    if (formErrors.interview_questions) {
      toast.error(lang.get('msg.fixValidationErrors'));

      // Automatically open the Discussion Guide section
      if (!isDiscussionGuideOpen) {
        handleToggleSection(EProjectDesignSection.DISCUSSION_GUIDE);
      }

      setTimeout(() => {
        if (discussionGuideSectionRef.current) {
          const firstErrorElement =
            discussionGuideSectionRef.current.querySelector('.validation-error');

          if (firstErrorElement) {
            firstErrorElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
          } else {
            discussionGuideSectionRef.current.scrollIntoView({
              behavior: 'smooth',
              block: 'start',
            });
          }
        }
      }, 300);
    }
  };

  const handleFormSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const shouldValidateInterviewQuestions =
      'interview_questions' in dirtyFields || reorderChanged || interviewQuestionsStructureChanged;

    const shouldValidateScreeningQuestions = 'screening_questions' in dirtyFields;

    const shouldValidatePrivacyPolicy = 'privacy_policy' in dirtyFields;

    const fieldsToValidate: Array<keyof ProjectCreationValues> = [
      ...(shouldValidatePrivacyPolicy ? (['privacy_policy'] as const) : []),
      ...(shouldValidateInterviewQuestions ? (['interview_questions'] as const) : []),
      ...(shouldValidateScreeningQuestions ? (['screening_questions'] as const) : []),
    ];

    if (!fieldsToValidate.length) {
      await onSubmit(getValues(), false);
      return;
    }

    const isValid = await trigger(fieldsToValidate);

    if (!isValid) {
      onError(errors);
      return;
    }

    await onSubmit(getValues(), false);
  };

  return (
    <main className="flex  w-full h-full -mt-2.5 relative overflow-y-hidden max-h-[90vh]">
      <form onSubmit={handleFormSubmit} className="flex flex-col w-full lg:w-1/2 h-screen z-5">
        <div className="fixed top-14 flex items-center gap-3">
          <h2 className="pb-2 mt-3 text-xl font-semibold epilogue-font">
            {lang.get('msg.design')}
          </h2>
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="lg:hidden mt-3"
            onClick={() => setTestChatSheetOpen(true)}
          >
            <MessageCircle className="h-4 w-4 mr-1.5" />
            {lang.get('msg.testChat')}
          </Button>
        </div>
        <div
          id="scrollContainer"
          className="z-10 fixed w-[calc(100%-32px)] lg:w-[48%] top-[105px] flex flex-col h-full gap-2 pr-4 overflow-y-auto pb-44"
        >
          <div className="flex flex-col w-full">
            {singleProjectData ? (
              <button
                type="button"
                className="flex items-center justify-start w-full gap-1.5 py-2 mb-4 border-b"
                onClick={() => handleToggleSection(EProjectDesignSection.INSTRUCTIONS)}
              >
                <IconSquareRoundedNumber1Filled
                  style={{ color: currentTheme?.primary }}
                  size={20}
                />
                <p>{lang.get('msg.instructions')}</p>
              </button>
            ) : (
              <div className="flex flex-col mt-2.5 mb-4">
                <div className="flex gap-1.5">
                  <Skeleton className="w-5 h-5 rounded-sm" />
                  <Skeleton className="h-5 rounded-sm w-28" />
                </div>
                <Skeleton className="w-full h-0.5 mt-2.5" />
              </div>
            )}
            {/* -- Instructions -- */}
            <div
              className={`transition-all h-full ease-in-out duration-700 flex flex-col overflow-hidden -mt-1 ${
                isInstructionsOpen ? 'max-h-[1500px]' : 'max-h-0'
              }`}
            >
              <Instructions
                singleProjectData={singleProjectData}
                isProjectDataLoading={isProjectDataLoading}
                isProjectPublished={isProjectPublished}
                isProjectCompleted={isProjectCompleted}
                register={register}
                control={control}
                setValue={setValue}
                errors={errors}
              />
            </div>
          </div>

          {/* -- Context -- */}
          <div className="flex flex-col w-full">
            <button
              type="button"
              className="flex items-center justify-start w-full gap-1.5 py-2 mb-4 border-b"
              onClick={() => handleToggleSection(EProjectDesignSection.CONTEXT)}
            >
              <IconSquareRoundedNumber2Filled style={{ color: currentTheme?.primary }} size={20} />
              {lang.get('msg.context')}
            </button>
            <div
              className={`transition-all ease-in-out duration-700 flex flex-col overflow-hidden -mt-1 ${
                isContextOpen ? 'max-h-[800px]' : 'max-h-0'
              }`}
            >
              <Context
                isProjectPublished={singleProjectData?.status === EProjectStatus.PUBLISHED}
                isProjectCompleted={singleProjectData?.status === EProjectStatus.COMPLETED}
                register={register}
                control={control}
                watch={watch}
                setValue={setValue}
              />
            </div>
          </div>

          {/* -- Screening Questions -- */}
          <div ref={screeningSectionRef} className="flex flex-col w-full" data-section="screening">
            <button
              type="button"
              className="flex items-center justify-start w-full gap-1.5 py-2 mb-4 border-b"
              onClick={() => handleToggleSection(EProjectDesignSection.SCREENING)}
            >
              <IconSquareRoundedNumber3Filled style={{ color: currentTheme?.primary }} size={20} />
              <span>{lang.get('msg.screening')}</span>

              <IconAlertCircle
                size={20}
                className={`ml-auto text-red-600 transition-opacity duration-300 ease-in-out delay-300 ${
                  showScreeningPanelWarning ? 'opacity-100' : 'opacity-0'
                }`}
              />
            </button>
            <div
              style={{
                maxHeight: isScreeningOpen
                  ? `${(screeningAnswersLength + screeningQuestionsLength) * 250}px`
                  : 0,
              }}
              className="flex flex-col -mt-1 overflow-hidden transition-all duration-1000 ease-in-out"
            >
              <ScreeningQuestions
                isProjectPublished={isProjectPublished}
                isProjectCompleted={isProjectCompleted}
                setValue={setValue}
                onSubmit={onSubmit}
                screeningQuestions={singleProjectData?.screening_questions}
                register={register}
                errors={errors}
                clearErrors={clearErrors}
              />
            </div>
          </div>

          {/* -- Discussion guide -- */}
          <div
            ref={discussionGuideSectionRef}
            className="flex flex-col w-full"
            data-section="discussion-guide"
          >
            <button
              type="button"
              className="flex items-center justify-start w-full gap-1.5 py-2 mb-4 border-b"
              onClick={() => handleToggleSection(EProjectDesignSection.DISCUSSION_GUIDE)}
            >
              <IconSquareRoundedNumber4Filled style={{ color: currentTheme?.primary }} size={20} />
              <span>{lang.get('msg.discussionGuide')}</span>

              <IconAlertCircle
                size={20}
                className={`ml-auto text-red-600 transition-opacity duration-300 ease-in-out delay-300 ${
                  showQuestionsPanelWarning ? 'opacity-100' : 'opacity-0'
                }`}
              />
            </button>

            <div
              style={{ maxHeight: isDiscussionGuideOpen ? `${1000 + questionsLength * 200}px` : 0 }}
              className="flex flex-col -mt-1 overflow-hidden transition-all duration-1000 ease-in-out"
            >
              <DiscussionGuide
                singleProjectData={singleProjectData}
                isProjectPublished={singleProjectData?.status === EProjectStatus.PUBLISHED}
                isProjectCompleted={singleProjectData?.status === EProjectStatus.COMPLETED}
                register={register}
                control={control}
                setValue={setValue}
                getValues={getValues}
                clearErrors={clearErrors}
                setFileData={setFileData}
                fileData={fileData}
                currentQuestions={currentQuestions}
                errors={errors}
                onSubmit={onSubmit}
              />
            </div>
          </div>

          {/* max respondents  */}
          <div className="flex flex-col w-full">
            <button
              type="button"
              className="flex items-center justify-start w-full gap-1.5 py-2 mb-4 border-b"
              onClick={() => handleToggleSection(EProjectDesignSection.MAX_RESPONDENTS)}
            >
              <IconSquareRoundedNumber5Filled style={{ color: currentTheme?.primary }} size={20} />
              {lang.get('msg.participants')}
            </button>
            <div
              className={`transition-all ease-in-out duration-700 flex flex-col overflow-hidden -mt-1 ${
                isMaxRespondentsOpen ? 'max-h-[800px]' : 'max-h-0'
              }`}
            >
              <ProjectShareMaxRespondets
                singleProjectData={singleProjectData}
                register={register}
                setValue={setValue}
                errors={errors}
                setIsFormChanged={setIsFormChanged}
              />
            </div>
          </div>
          <div
            style={{ backgroundColor: currentTheme?.background }}
            className="fixed bottom-0 w-full lg:w-1/2 h-[66px] px-4 z-100 left-0"
          >
            <Button type="submit" className="w-full mt-2" disabled={isSubmitDisabled}>
              {/* Icon rendering */}
              {isPending && <IconLoader2 className="mr-1.5 animate-spin" size={20} />}

              {isSuccessWithoutChanges && <IconCircleCheck className="mr-1.5" size={20} />}

              {(isFormChanged || reorderChanged) && !isPending && status !== 'success' && (
                <IconDeviceFloppy className="mr-1.5" size={20} />
              )}

              {/* Label rendering */}
              {isIdleWithoutChanges && <span>{lang.get('msg.noChanges')}</span>}

              {isSuccessWithoutChanges && <span>{lang.get('msg.allSaved')}</span>}

              {(isFormChanged || reorderChanged) && <span>{lang.get('msg.saveChanges')}</span>}
            </Button>
          </div>
        </div>
      </form>

      {/* chat side — inline at lg+, Sheet slide-over below lg */}
      {isLg && (
        <div className="fixed right-0 w-1/2 h-full">
          <h2 className="mt-3 ml-8 text-xl font-semibold capitalize epilogue-font">
            {lang.get('msg.testChat')}
          </h2>
          <div
            style={{ backgroundColor: currentTheme?.background }}
            className="w-[98%] h-screen pb-20 pl-3"
          >
            {!isProjectDataLoading ? (
              <DashboardTestChat
                projectId={projectId}
                chatHeight="min-h-[calc(100dvh-190px)] max-h-[calc(100dvh-190px)]"
                singleProjectData={singleProjectData}
              />
            ) : (
              <TestChatSkeletonLoading />
            )}
          </div>
        </div>
      )}

      {/* Below lg, render the test chat in a right-side Sheet triggered
          by the button in the form header. modal={false} keeps the form
          interactive while the chat is open. */}
      {!isLg && (
        <Sheet
          open={testChatSheetOpen}
          onOpenChange={setTestChatSheetOpen}
          modal={false}
        >
          <SheetContent
            side="right"
            className="w-full sm:w-[440px] sm:min-w-[380px] max-w-[600px] p-0 flex flex-col z-[100]"
          >
            <SheetTitle className="px-5 pt-4 pb-2 text-lg font-semibold capitalize epilogue-font">
              {lang.get('msg.testChat')}
            </SheetTitle>
            <div
              style={{ backgroundColor: currentTheme?.background }}
              className="flex-1 px-4 pb-4 overflow-hidden"
            >
              {!isProjectDataLoading ? (
                <DashboardTestChat
                  projectId={projectId}
                  chatHeight="min-h-[calc(100dvh-160px)] max-h-[calc(100dvh-160px)]"
                  singleProjectData={singleProjectData}
                />
              ) : (
                <TestChatSkeletonLoading />
              )}
            </div>
          </SheetContent>
        </Sheet>
      )}
    </main>
  );
}

export default ProjectCreate;
